"use client";

import {
    createContext,
    FC,
    memo,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useContext,
    useMemo,
} from "react";
import { IEventFeedResponse, ISportFeedResponse } from "@finbackoffice/websocket-client";
import { IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import {
    FeedRidContext,
    SportFavoritesActionsContext,
    IMainOddsState,
    useMainMarketOdds,
} from "@finbackoffice/site-core";
import { MarketStatus } from "@finbackoffice/enums";
import dynamic from "next/dynamic";
import { useSocketStorage, useDate, useSportFeedType, useBetSlipOutcomeHandler } from "hooks";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import PreloadComponent from "components/base/preload-component/PreloadComponent";

const EventWithWidget = dynamic(() => import("./layouts/event-with-widget/EventWithWidget"), {
    loading: () => <PreloadComponent style={{ height: 136, margin: "0 6px 6px" }} />,
    ssr: false,
});

const Default = dynamic(() => import("./layouts/default/Default"), {
    loading: () => <PreloadComponent style={{ height: 114, margin: "0 6px 6px" }} />,
    ssr: false,
});

const Flex = dynamic(() => import("./layouts/flex/Flex"), {
    loading: () => <PreloadComponent style={{ height: 97, margin: "0 6px 6px" }} />,
    ssr: false,
});

export enum LiveTournamentGameLayoutEnum {
    Default = "default",
    Flex = "flex",
    Widget = "widget",
}

interface IProps {
    game: IEventFeedResponse;
    layout?: LiveTournamentGameLayoutEnum;
    isTwoColumns?: boolean;
}

type ILiveTournamentGameContext = IProps & {
    formatDate: (date: number | Date, formatStr: string) => string;
    getSportById: (id: number) => ISportFeedResponse | null | undefined;
    handleStarClick: (event: SyntheticEvent) => void;
    columnCount: number;
    mainOdds: IMainOddsState | undefined;
    mainMarketStatus?: MarketStatus;
    mainMarketId: number | null;
    mainMarketName: string;
    onOutcomeClick: (
        marketId: number | null,
        outcome: IMarketOutcomeFeedResponse | null,
        marketName?: string | null,
        marketStatus?: MarketStatus,
    ) => void;
    renderCricketBat: ReactNode;
};

export const LiveTournamentGameContext = createContext<ILiveTournamentGameContext>(null as any);

const LiveTournamentGame: FC<IProps> = ({
    game,
    isTwoColumns,
    layout = LiveTournamentGameLayoutEnum.Default,
}) => {
    const { liveRid } = useContext(FeedRidContext);
    const { handleFavClick } = useContext(SportFavoritesActionsContext);

    const { formatDate } = useDate();
    const { getSportById } = useSocketStorage(liveRid);
    const { isBCFeed } = useSportFeedType();

    const { mainOdds, mainMarketStatus, mainMarketId, columnCount, mainMarketName } =
        useMainMarketOdds(
            game.sport_id,
            game.markets,
            isBCFeed,
            game.home_team?.name || "",
            game.away_team?.name || "",
        );

    const onOutcomeClick = useBetSlipOutcomeHandler({
        gameId: game.id,
        homeName: game.home_team?.name || "",
        awayName: game.away_team?.name || "",
        gameStatus: game.status,
    });

    const handleStarClick = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();
            handleFavClick(game.id, game.sport_id, game.tournament_id, game.scheduled_at || "");
        },
        [game.id, game.sport_id, game.tournament_id, game.scheduled_at, handleFavClick],
    );

    const renderLayout = useMemo(() => {
        switch (layout) {
            case LiveTournamentGameLayoutEnum.Flex:
                return <Flex />;
            case "widget":
                return <EventWithWidget />;
            default:
                return <Default />;
        }
    }, [layout]);

    const renderCricketBat = useMemo(
        () => (
            <Svg
                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/cricket-bat.svg`}
                className="svg-icon"
            />
        ),
        [],
    );

    const value = useMemo(
        () => ({
            game,
            formatDate,
            getSportById,
            mainOdds,
            columnCount,
            onOutcomeClick,
            handleStarClick,
            mainMarketStatus,
            mainMarketId,
            mainMarketName,
            isTwoColumns,
            renderCricketBat,
        }),
        [
            game,
            formatDate,
            getSportById,
            mainOdds,
            columnCount,
            onOutcomeClick,
            handleStarClick,
            mainMarketStatus,
            mainMarketId,
            mainMarketName,
            isTwoColumns,
            renderCricketBat,
        ],
    );

    return (
        <LiveTournamentGameContext.Provider value={value}>
            {renderLayout}
        </LiveTournamentGameContext.Provider>
    );
};

export default memo(LiveTournamentGame);
